/**
 * The portal's read hooks.
 *
 * <p>Two reads make the whole shell work: `GET /internal/v0/me` resolves WHO is signed in
 * and WHICH tenant they may read, and `GET /internal/v0/reporting/families?tenant=…`
 * resolves WHAT reports that tenant has configured. Everything the nav shows is derived
 * from those two answers — nothing about the client is hard-coded into this bundle.</p>
 *
 * <p>WHY THE REPORT HOOKS LIVE HERE, not in a `reportingQueries.ts` sibling: phase 336's
 * plan named that filename before phase 342 (#1160) had landed the portal, and what #1160
 * actually landed put `useWeeklyActivity` / `useForecasting` in THIS module alongside the
 * identity rail they are gated by. The plan's own portal-root-resolution rule says its
 * paths are normative relative to what landed, so task 2.1 extends the landed module rather
 * than opening a second one — a sibling file would have to re-declare `useWeeklyActivity`
 * and `REPORTING_QUERY_OPTIONS`, and two copies of a cadence policy is exactly how one of
 * them silently starts polling.</p>
 *
 * <p>Phase 530 wave 4 task 4.1 (#1939) adds the onboarding status board's three reads for the
 * same reason, and it is the same argument a second time: they are tenant-scoped reads gated by
 * the same identity rail and carrying the same cadence policy, and a `jobStatusQueries.ts`
 * sibling would have to import both of those or re-declare them. What they are NOT is
 * reporting: they read live workflow state, not a fact store, which is why they carry their own
 * section below and their own note about why the cadence is nevertheless the right one.</p>
 */
import { useContext, useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { ApiError, fetchWithAuth } from "./fetcher.js";
import type {
  ActionErrorBody,
  EmailPreviewResponse,
  EmailSaveErrorBody,
  EmailSaveResponse,
  EmailTemplatesResponse,
  SetFieldRequest,
  SignalNowRequest,
  ForecastRolesResponse,
  ForecastingRollupResponse,
  ImportApplyResponse,
  ImportDryRunResponse,
  JobStatusDetailResponse,
  JobStatusFilesResponse,
  JobStatusOutstandingResponse,
  MeResponse,
  ReportingFamiliesResponse,
  ReportingRosterResponse,
  TenantSummary,
  WeeklyActivityGridResponse,
} from "./types.js";
import {
  TenantSelectionContext,
  resolveTenantId,
  tenantOptions,
  tenantSummary,
} from "./tenantSelection.js";
import { EMAILS_ENTRY, navEntriesFor, type PortalFamilyEntry } from "../nav/familyRegistry.js";

/**
 * Reporting cadence, NOT ops cadence.
 *
 * <p>The operator console polls every 4s (its `POLL_OPTIONS`, queries.ts:40) because an
 * operator is watching jobs move. Nothing here moves like that: report facts are written by
 * a weekly scheduled run, and a client's configured families change when someone deploys a
 * workflow for them. So the portal takes a 60s stale window plus a refetch when the tab is
 * focused again, and NO polling interval at all — an interval here would re-request
 * identical bytes ~900 times an hour and, on the reporting reads, would drive a third-party
 * roster lookup server-side each time.</p>
 *
 * <p>Spread onto every hook rather than left to the app's QueryClient defaults, so a hook
 * carries its own cadence into whatever client mounts it (including a test's).</p>
 *
 * <p>THE ONBOARDING BOARD KEEPS THIS CADENCE TOO, and that is a decision rather than an
 * inheritance (phase 530 wave 4 task 4.1 / Design §6). Card #1939 asks for a "live bar" on a
 * board of onboarding files, and the operator console answers that shape of question with a 4 s
 * poll (`apps/operator-console/src/api/queries.ts`'s `POLL_OPTIONS`) because an operator is watching
 * work move. A staffer reading the morning's files is not: they open the board, read it, and
 * come back to the tab later. So "live" here is the FOCUS REFETCH BELOW plus an explicit
 * refresh control and a visible `as_of` on the page — NOT an interval. An interval would put a
 * repeating per-viewer query on the engine for every tab every staffer in the firm leaves open,
 * to re-render bytes that change when someone submits a form. If you are about to "fix" this by
 * adding `refetchInterval`, that is the thing being declined.</p>
 */
export const REPORTING_QUERY_OPTIONS = {
  staleTime: 60_000,
  refetchOnWindowFocus: true,
  refetchInterval: false,
  refetchIntervalInBackground: false,
} as const;

/**
 * Build a tenant-scoped engine path. The ONE place this app turns a tenant id into a
 * querystring.
 *
 * <p>Tenant scoping is a platform invariant, not a per-hook habit: every read names its
 * tenant explicitly and there is no default tenant anywhere in the platform. Funnelling all
 * four reporting reads through one builder means the invariant is a single line of code that
 * a test can pin, rather than four hand-written `URLSearchParams` that stay correct only for
 * as long as everyone remembers.</p>
 *
 * <p>The `?? ""` is unreachable in practice — every caller is `enabled`-gated on a resolved
 * tenant — but an empty `tenant=` is the right shape for the impossible case anyway: the
 * engine answers it 400 `invalid_tenant`, which is a loud failure rather than a read that
 * quietly means "whatever tenant the server feels like".</p>
 *
 * @param extra further query params for reads that take one (the board's `include_terminal`).
 *              They are appended AFTER `tenant`, so every URL this app builds still begins the
 *              same way — and they go through this builder rather than round a second
 *              `URLSearchParams` at the call site, which is what keeps the tenant invariant a
 *              single line of code a test can pin.
 */
function tenantScoped(
  path: string,
  tenantId: string | null | undefined,
  extra?: Readonly<Record<string, string>>,
): string {
  const qs = new URLSearchParams({ tenant: tenantId ?? "" });
  for (const [key, value] of Object.entries(extra ?? {})) qs.set(key, value);
  return `${path}?${qs.toString()}`;
}

/**
 * A signed-in principal the portal cannot serve: authenticated, but with no tenant behind
 * them, so there is nothing they are allowed to read.
 *
 * <p>This is a provisioning fault, not a user error — `AddOperatorTool` refuses to create a
 * client with an empty tenant array, so reaching this state means the row was made some
 * other way. It gets a LOUD, distinct error state rather than silently rendering the same
 * "no reports configured yet" empty state a correctly-provisioned client would see: those
 * two situations need completely different fixes, and a shared empty state would hide the
 * difference from whoever is called about it.</p>
 */
export class NoTenantAssignedError extends Error {
  constructor(public readonly email: string) {
    super(`client-portal: signed-in principal ${email} has no allowed tenant`);
    this.name = "NoTenantAssignedError";
  }
}

/** `GET /internal/v0/me` — identity + the tenants this principal may read. */
export function useMe(): UseQueryResult<MeResponse, Error> {
  return useQuery({
    queryKey: ["me"] as const,
    queryFn: () => fetchWithAuth<MeResponse>("/internal/v0/me"),
    ...REPORTING_QUERY_OPTIONS,
  });
}

/** The resolved client identity: who they are, and the tenant they are reading. */
export interface ClientIdentity {
  isLoading: boolean;
  /** A failed `/me`, or {@link NoTenantAssignedError} when the principal has no tenant. */
  error: Error | null;
  /** The selected tenant's UUID, or null until it resolves (or if there is none). */
  tenantId: string | null;
  /** The selected tenant's display name, for page furniture. Null until `/me` resolves. */
  tenantName: string | null;
  /** The selected tenant's slug — what `?tenant=` carries. Null until `/me` resolves. */
  tenantSlug: string | null;
  /**
   * The tenants this principal may switch between (walk 3). Empty or one entry for a client —
   * which is exactly when the switcher does not render.
   */
  tenants: TenantSummary[];
  /** The signed-in email, shown in the nav footer. Null until `/me` resolves. */
  email: string | null;
}

/**
 * The ADR-081 identity rail, client side: every subsequent read passes ONE tenant explicitly.
 *
 * <p>For a client principal (exactly one allowed tenant) that tenant is `allowed_tenant_ids[0]`
 * and nothing else is consulted — no `?tenant=`, no storage. Walk 3 added the multi-tenant case:
 * an operator or platform admin signed in to the portal reads the tenant chosen in the switcher
 * (`nav/TenantSelection.tsx`, carried in the URL), resolved by `resolveTenantId` against their
 * OWN allowed set, and defaulting to `allowed_tenant_ids[0]`. There is still no fallback to a
 * platform default — zero tenants surfaces as an error rather than resolving to something.</p>
 */
export function useClientTenant(): ClientIdentity {
  const me = useMe();
  const selection = useContext(TenantSelectionContext);
  const data = me.data;

  const tenantId = resolveTenantId(data, selection?.requested ?? null);
  const hasNoTenant = data !== undefined && tenantId === null;
  const summary = tenantSummary(data, tenantId);

  useEffect(() => {
    if (!hasNoTenant || !data) return;
    // eslint-disable-next-line no-console
    console.error(
      `client-portal: ${data.email} signed in with an empty allowed_tenant_ids — ` +
        "no reports can be read. Re-provision the user with add_operator " +
        "(role=client, allowed_tenant_ids=[<tenant uuid>]).",
    );
  }, [hasNoTenant, data]);

  return {
    isLoading: me.isLoading,
    error: me.error ?? (hasNoTenant && data ? new NoTenantAssignedError(data.email) : null),
    tenantId,
    // Looked up BY ID: `/me` orders allowed_tenants by slug, so `[0]` is not the default tenant's
    // name for a principal with more than one.
    tenantName: summary?.display_name ?? null,
    tenantSlug: summary?.slug ?? null,
    tenants: tenantOptions(data),
    email: data?.email ?? null,
  };
}

/**
 * `GET /internal/v0/reporting/families?tenant=<uuid>` — the tenant's configured report
 * families (ACTIVE timer workflow_versions, the scheduler's own discovery artifact).
 *
 * <p>The tenant is always in the querystring, never implied by the session: the engine
 * requires it, the tenant guard checks it against the principal's allowed set, and it is
 * part of the query key so a cached answer can never be shown for the wrong tenant. The
 * hook stays disabled until it has one.</p>
 */
export function useFamilies(
  tenantId: string | null | undefined,
): UseQueryResult<ReportingFamiliesResponse, Error> {
  return useQuery({
    queryKey: ["reporting-families", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<ReportingFamiliesResponse>(
        tenantScoped("/internal/v0/reporting/families", tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

/**
 * `GET /internal/v0/reporting/weekly-activity?tenant=<uuid>` — the tenant's weekly sales
 * activity history (the `weekly-sales-activity` family), as long-form value cells.
 *
 * <p>The console's equivalent hook (reportingQueries.ts:64-77) polls on a 60s interval; this
 * one does not poll AT ALL. The engine resolves a consultant roster through a third-party
 * connector on every one of these reads, so an interval here would spend someone else's rate
 * limit to re-render bytes that change once a week. The 60s stale window plus a refetch when
 * the tab regains focus gives a client who leaves the page open all day fresh numbers the
 * moment they look back at it, and no traffic at all while they are not.</p>
 *
 * <p>Tenant discipline is identical to {@link useFamilies}: explicit in the querystring,
 * part of the query key, hook disabled until it resolves. A cached grid can never be shown
 * under another tenant's identity.</p>
 */
export function useWeeklyActivity(
  tenantId: string | null | undefined,
): UseQueryResult<WeeklyActivityGridResponse, Error> {
  return useQuery({
    queryKey: ["weekly-activity", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<WeeklyActivityGridResponse>(
        tenantScoped("/internal/v0/reporting/weekly-activity", tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

/**
 * `GET /internal/v0/reporting/forecasting?tenant=<uuid>` — the tenant's vacancy forecast
 * (the `sales-forecast` family), as (month × metric_key) rollup cells.
 *
 * <p>No polling, for the same reason {@link useWeeklyActivity} does not poll: the facts are
 * written by a scheduled report run, so an interval would re-request bytes that change once
 * a week. The 60s stale window plus a refetch on tab focus is the whole cadence.</p>
 *
 * <p>Tenant discipline is identical to the other reads: explicit in the querystring, part of
 * the query key, hook disabled until it resolves — a cached forecast can never be shown
 * under another tenant's identity.</p>
 *
 * <p>The response's `as_of` is NULLABLE (unlike the weekly grid's): an empty fact store is a
 * 200 with `as_of: null`, which the Vacancies page renders as its empty state.</p>
 */
export function useForecasting(
  tenantId: string | null | undefined,
): UseQueryResult<ForecastingRollupResponse, Error> {
  return useQuery({
    queryKey: ["forecasting-rollup", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<ForecastingRollupResponse>(
        tenantScoped("/internal/v0/reporting/forecasting", tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

/**
 * `GET /internal/v0/reporting/roster?tenant=<uuid>` — the tenant's LIVE consultant roster:
 * `loxo_user_id` → name, plus the tenant's support-consultant flag.
 *
 * <p>This is the read that turns a fact-store split into something a person can read. The
 * weekly grid is keyed by `loxo_user_id` and carries NO names (ADR-074 D1 — ids at rest,
 * attributes live), so without this every per-consultant row would render a bare numeric id.
 * `is_support` comes from the same tenant config the forecast's attribution rule reads, so
 * the dimming on one page and the attribution on the other cannot disagree about who is
 * support.</p>
 *
 * <p>Cadence and tenant discipline are identical to the fact reads: no polling, a 60s stale
 * window, tenant explicit in the querystring and in the query key, disabled until it
 * resolves. Ordering (`loxo_user_id` ascending) is the ENGINE's, and is what keeps a
 * consultant on the same categorical colour week after week — consumers must not re-sort by
 * activity.</p>
 */
export function useRoster(
  tenantId: string | null | undefined,
): UseQueryResult<ReportingRosterResponse, Error> {
  return useQuery({
    queryKey: ["reporting-roster", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<ReportingRosterResponse>(
        tenantScoped("/internal/v0/reporting/roster", tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

/**
 * `GET /internal/v0/reporting/forecast-roles?tenant=<uuid>` — the ROLE-LEVEL forecast
 * payload (#1161 / phase 338 wave 3): one row per open vacancy, with its title, company,
 * attributed consultant, priority letter, estimated value, estimated month and overdue flag.
 *
 * <p>This — not {@link useForecasting} — is what the Vacancies page renders. The older
 * rollup sums facts per (month, metric_key) with scope and priority already collapsed, so
 * role rows, consultant splits and A/R/B/C chips cannot be re-derived from it at all. Both
 * hooks exist for now because the interim page still reads the rollup; the page rewrite that
 * consumes this one retires that call site with it.</p>
 *
 * <p>The payload is computed LIVE (it carries attributes facts do not hold), which also
 * means it FAILS LOUD: a provider or compute failure is a 5xx, never an empty-but-200. So
 * `roles: []` can be rendered honestly as "no open vacancies" instead of being ambiguous
 * with "Loxo was down" — the reason no polling interval is wanted here either.</p>
 */
export function useForecastRoles(
  tenantId: string | null | undefined,
): UseQueryResult<ForecastRolesResponse, Error> {
  return useQuery({
    queryKey: ["forecast-roles", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<ForecastRolesResponse>(
        tenantScoped("/internal/v0/reporting/forecast-roles", tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

// ── Phase 530 wave 4 task 4.1 (#1939) — the onboarding status board's three reads ────────
//
// The same three disciplines every read above follows, and for the same reasons:
//
//   • TENANT EXPLICIT. `tenantScoped()` builds the path, the tenant is in the query KEY, and
//     the hook stays disabled until it resolves — so a cached answer can never be shown under
//     another tenant's identity, and no read fires as `tenant=` while `/me` is still in flight.
//   • REPORTING CADENCE, NOT AN OPS POLL. See REPORTING_QUERY_OPTIONS above, which now carries
//     this board's own reason for declining `refetchInterval`.
//   • NO ADAPTER. The response type is the snake_case wire mirror, handed to the page as the
//     engine sent it (`types.ts:11-14`).
//
// These three are ONE READ MODEL on the engine side (phase 528's `IJobStatusViewReader`, which
// the staff agent reads through too), re-labelled onto three routes. So they are three hooks
// here, not one composed read: the board, one file and the cross-file chase list are asked at
// three different scopes, and a page that wanted one of them must not pay for the other two.

/** The engine's client-readable status-board routes. One prefix, three reads. */
const JOB_STATUS_PATH = "/internal/v0/job-status";

/** What a board read is asked for. Every member is part of the cache key. */
export interface JobStatusBoardOptions {
  /**
   * #2508 (A1) — the declaration whose board this is (the families row's `report_key`). Every
   * board read carries it; `null` only for an engine older than #2508, whose families row names
   * no workflow and whose board read defaults the spine itself.
   */
  workflowId?: string | null;
  /** #2508 — a DECLARED view name (`status_view.views[].name`), or none for every file. */
  view?: string | null;
  /** Ask for finished files too — the engine leaves them out unless asked. */
  includeTerminal?: boolean;
  /** Can only NARROW the tenant gate, never open it. */
  enabled?: boolean;
}

/** The board read: every page fetched so far, merged into one envelope, plus "Show more". */
export interface JobStatusBoardResult {
  /** Every page so far as ONE envelope: files in arrival order, `next_cursor` from the last page. */
  data: JobStatusFilesResponse | undefined;
  error: Error | null;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
  /** The last page carried a cursor, so there are more files than are listed. */
  hasMore: boolean;
  /** Fetch the next page. A no-op while one is already on its way or there is none. */
  showMore: () => void;
  isLoadingMore: boolean;
}

/** One envelope out of the pages read so far. A file seen twice (it moved mid-paging) is kept once. */
export function mergeBoardPages(
  pages: readonly JobStatusFilesResponse[] | undefined,
): JobStatusFilesResponse | undefined {
  const first = pages?.[0];
  if (pages === undefined || first === undefined) return undefined;
  const last = pages[pages.length - 1] ?? first;
  const seen = new Set<string>();
  const files = pages
    .flatMap((page) => page.files ?? [])
    .filter((file) => (seen.has(file.job_id) ? false : (seen.add(file.job_id), true)));
  return { ...first, files, next_cursor: last.next_cursor ?? null };
}

/**
 * `GET /internal/v0/job-status?tenant=<uuid>&workflow_id=<id>` — a status BOARD: one row per file
 * (or client), in the words the tenant declared.
 *
 * <p>#2508 (plan amendment A1): CURSOR PAGED. The engine answers a page at a time with a
 * `next_cursor`; this hook keeps every page fetched so far and hands the page ONE merged envelope,
 * so "Show more" appends rather than replaces. The 200-row cap and its disclosure are gone with
 * it.</p>
 *
 * <p>`workflowId`, `view` and `includeTerminal` are REQUEST params, and all three are in the query
 * key for the same reason the tenant is: they are different answers, and one must never be served
 * from another's cache entry. The view NAME is a declared name the engine resolves against the
 * declaration; the portal never sends a path or a filter expression.</p>
 */
export function useJobStatusFiles(
  tenantId: string | null | undefined,
  options?: JobStatusBoardOptions,
): JobStatusBoardResult {
  const workflowId = options?.workflowId ?? null;
  const view = options?.view ?? null;
  const includeTerminal = options?.includeTerminal ?? false;
  // `enabled` can only NARROW the tenant gate, never open it (walk 3: the board holds its read
  // for a tenant whose families say it has no such board, and holds its "completed" probe until
  // the open-files answer is known to be empty).
  const enabled = !!tenantId && (options?.enabled ?? true);
  const query = useInfiniteQuery({
    queryKey: ["job-status-files", tenantId, workflowId, view, includeTerminal] as const,
    queryFn: ({ pageParam }) => {
      const extra: Record<string, string> = {};
      if (workflowId !== null) extra.workflow_id = workflowId;
      if (view !== null) extra.view = view;
      if (includeTerminal) extra.include_terminal = "true";
      if (pageParam !== null) extra.cursor = pageParam;
      return fetchWithAuth<JobStatusFilesResponse>(tenantScoped(JOB_STATUS_PATH, tenantId, extra));
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last: JobStatusFilesResponse) => last.next_cursor ?? undefined,
    ...REPORTING_QUERY_OPTIONS,
    enabled,
  });
  const data = useMemo(() => mergeBoardPages(query.data?.pages), [query.data]);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = query;
  return {
    data,
    error: query.error,
    isFetching: query.isFetching,
    refetch,
    hasMore: hasNextPage,
    showMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
    isLoadingMore: isFetchingNextPage,
  };
}

/**
 * `GET /internal/v0/job-status/{jobId}?tenant=<uuid>` — ONE file, in full: the header, the
 * milestone timeline, the party ledger, what is outstanding and the risk assessment when there
 * is one.
 *
 * <p>ONE READ, ONE ENVELOPE — so this hook takes no companion reads. Everything the detail page
 * renders arrives together, which is what stops the page assembling itself from three answers
 * that can disagree about one file.</p>
 *
 * <p>TENANT FIRST, THEN THE FILE, matching every other hook in this module: the file id names
 * WHAT to read and the tenant names WHOSE, and the second is the invariant. The hook is
 * disabled until it has both — a route param is `undefined` on the first render of a
 * freshly-mounted route, and a read that fired then would ask the engine for a file called
 * `undefined`.</p>
 *
 * <p>A 404 is the answer for a file that does not exist AND for one belonging to another
 * tenant, by construction on the engine side. The page must therefore say "we could not find
 * that file" and never "you are not allowed to see that file" — it does not know which, and
 * neither does the engine's answer.</p>
 */
export function useJobStatusDetail(
  tenantId: string | null | undefined,
  jobId: string | null | undefined,
): UseQueryResult<JobStatusDetailResponse, Error> {
  return useQuery({
    queryKey: ["job-status-detail", tenantId, jobId] as const,
    queryFn: () =>
      fetchWithAuth<JobStatusDetailResponse>(
        tenantScoped(`${JOB_STATUS_PATH}/${encodeURIComponent(jobId ?? "")}`, tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId && !!jobId,
  });
}

/**
 * `GET /internal/v0/job-status/outstanding?tenant=<uuid>` — the CHASE LIST: everything the firm
 * is still owed, ACROSS files, oldest owed first.
 *
 * <p>A separate read from the board rather than a derivation over it, because it is asked at a
 * scope the board cannot reach: the board is scoped to ONE declared spine by construction,
 * while a firm chases what IT is owed across every workflow it runs. Summing the board's
 * `outstanding_count` column would answer a narrower question and quietly call it the wider
 * one.</p>
 *
 * <p>An empty `items` list is a 200 and a real answer — "nothing is outstanding" — never an
 * error and never a 404.</p>
 */
export function useJobStatusOutstanding(
  tenantId: string | null | undefined,
): UseQueryResult<JobStatusOutstandingResponse, Error> {
  return useQuery({
    queryKey: ["job-status-outstanding", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<JobStatusOutstandingResponse>(
        tenantScoped(`${JOB_STATUS_PATH}/outstanding`, tenantId),
      ),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId,
  });
}

// ── Walk 4 (#2268) / #3357 — the staff actions, ONE generic post ─────────────────────────────
//
//   POST /internal/v0/jobs/{jobId}/actions/<the action id, `_` spelled `-`>
//
// #3357: the page names no action route. Every staff action reference the engine sends is posted
// to its own id's route (`clear_party` → `actions/clear-party`) — the one rule the engine's route
// table is held to (StaffActionRouteRuleTests). What the body carries is the reference's own
// `target` plus what its dialog collected (a reason, an email, the look-through record), and the
// route forwards only the fields it takes, so a forged body still meets the tool's checks.
//
// The tenant rides on the querystring like every read's — the engine takes the file's tenant from
// the job and guards it against the caller, and naming it here keeps "every request names its
// tenant" a single line of code (`tenantScoped`). Who MAY act is decided by the engine, per
// action, on the route: the portal only draws a button the read said was `allowed`, and a refusal
// still comes back as a 403 the page turns into words.

/** The contract's action routes. */
function jobActionPath(jobId: string, action: string): string {
  return `/internal/v0/jobs/${encodeURIComponent(jobId)}/actions/${action}`;
}

function postAction<TResponse>(
  tenantId: string,
  jobId: string,
  action: string,
  body: unknown,
): Promise<TResponse> {
  return fetchWithAuth<TResponse>(tenantScoped(jobActionPath(jobId, action), tenantId), {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** #3357 — a staff action reference's id as its route segment: `clear_party` → `clear-party`. */
export function staffActionRoute(action: string): string {
  return action.replaceAll("_", "-");
}

/** #3357 — one staff action, by its reference's id, and the body its dialog built. */
export interface StaffActionPost {
  action: string;
  body: Record<string, unknown>;
}

/**
 * #3357 — post ONE staff action to its own route. The answer is whatever the engine sent (a
 * reminder's `{ sent, skipped }`, a look-through's `{ added_parties, … }`, a person's
 * `{ party_key, status }`); the page reads it by its shape.
 */
export function postStaffAction(tenantId: string, jobId: string, post: StaffActionPost): Promise<unknown> {
  return postAction<unknown>(tenantId, jobId, staffActionRoute(post.action), post.body);
}

// ── #2508 — the two GENERIC declared record actions (plan amendment A2) ─────────────────────
//
//   POST /internal/v0/jobs/{jobId}/actions/set-field    { action, value, reason? }
//   POST /internal/v0/jobs/{jobId}/actions/signal-now   { action }
//
// The page sends a DECLARED action's name and nothing else that says what to change: which ctx path
// a set-field writes, which flag it clears and which trigger a signal fires all come from the
// declaration on the engine side, never from the request. The role check is the tool's (L-64).

/** Set a declared field (e.g. Change review date), with the staff member's reason. */
export function setJobField(tenantId: string, jobId: string, body: SetFieldRequest): Promise<unknown> {
  return postAction<unknown>(tenantId, jobId, "set-field", body);
}

/** Fire a declared action now (e.g. Send re-confirm links now). */
export function signalJobNow(tenantId: string, jobId: string, body: SignalNowRequest): Promise<unknown> {
  return postAction<unknown>(tenantId, jobId, "signal-now", body);
}

// ── #2508 — the declared row import ─────────────────────────────────────────────────────────
//
//   POST /internal/v0/jobs/import?tenant=<uuid>&workflow_id=<id>&dry_run=true|false   (text/csv)
//
// The file goes up as the staffer chose it; the engine reads it against the workflow's DECLARED
// `import:` block (which column is the key, how each header maps, the value maps). The dry run saves
// nothing; the apply starts one record per ready row, skipping keys already imported.

/** The largest register one import takes (the contract's multipart cap). Checked here first. */
export const IMPORT_MAX_BYTES = 2 * 1024 * 1024;

function importPath(tenantId: string, workflowId: string, dryRun: boolean): string {
  return tenantScoped("/internal/v0/jobs/import", tenantId, {
    workflow_id: workflowId,
    dry_run: dryRun ? "true" : "false",
  });
}

/** Check a register: what an import would do. Nothing is saved. */
export function checkImport(tenantId: string, workflowId: string, csv: string): Promise<ImportDryRunResponse> {
  return fetchWithAuth<ImportDryRunResponse>(importPath(tenantId, workflowId, true), {
    method: "POST",
    headers: { "Content-Type": "text/csv" },
    body: csv,
  });
}

/** Import a register: one record per ready row. */
export function applyImport(tenantId: string, workflowId: string, csv: string): Promise<ImportApplyResponse> {
  return fetchWithAuth<ImportApplyResponse>(importPath(tenantId, workflowId, false), {
    method: "POST",
    headers: { "Content-Type": "text/csv" },
    body: csv,
  });
}

/** "a", "a or b", "a, b or c" — who CAN, in a sentence. */
export function joinOr(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/** What went wrong with an action, in the words the page shows. */
export interface ActionFailure {
  kind: "role_required" | "already_done" | "not_possible" | "not_found" | "rejected" | "failed";
  message: string;
}

/**
 * Turn an action's error into the words the page shows. The engine's shapes (`error` and
 * `error_code` carry the same code; the code is read from `error_code`, falling back to `error`):
 * 403 `role_required` names who CAN (`holders`); 409 `not_open` / `settled_by_other_action` is
 * "already done" (someone, or another action, settled it — the file is refetched either way),
 * `not_staff_settleable` is an item only its form settles, `job_terminal` a closed file; 404 is a
 * job, party, item or undeclared action that is not there (or not this tenant's — the engine
 * never says which); 400 is a request it refused.
 */
export function describeActionError(error: unknown): ActionFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "failed", message: "That did not go through. Please try again." };
  }
  let body: ActionErrorBody = {};
  try {
    body = JSON.parse(error.bodyText) as ActionErrorBody;
  } catch {
    body = {};
  }
  if (error.status === 403) {
    const holders = (body.holders ?? []).filter((holder) => holder.trim() !== "");
    const who =
      holders.length > 0
        ? joinOr(holders)
        : body.required_role
          ? `people with the ${body.required_role} role`
          : "people with the right role";
    return { kind: "role_required", message: `Only ${who} can do this` };
  }
  if (error.status === 409) {
    const code = body.error_code ?? body.error;
    if (code === "job_terminal") {
      return {
        kind: "not_possible",
        message: "This file is closed, so nothing more can be done on it.",
      };
    }
    if (code === "not_staff_settleable") {
      return {
        kind: "not_possible",
        message:
          "This item is settled when its form comes in, so it cannot be marked received by hand.",
      };
    }
    // #2404 — the per-person actions' refusals, each in words (the file is refetched either way).
    if (code === "party_not_failed") {
      return {
        kind: "not_possible",
        message: "There is no identity check of theirs to run again: it passed, was cleared, or has not been sent yet.",
      };
    }
    if (code === "party_removed_by_client") {
      return {
        kind: "not_possible",
        message: "The client removed this person from their form. Restore them first, then add their email.",
      };
    }
    if (code === "no_client_removed_parties") {
      return { kind: "already_done", message: "Nobody the client removed is waiting on this file now." };
    }
    if (code === "party_not_client_removed") {
      return { kind: "already_done", message: "This person is no longer marked as removed by the client." };
    }
    if (code === "party_not_awaiting_contact") {
      return { kind: "already_done", message: "This person is no longer waiting for an email address." };
    }
    // `not_open`, `settled_by_other_action`, `party_already_settled`, and any 409 the engine adds
    // later.
    return { kind: "already_done", message: "Already done" };
  }
  if (error.status === 502) {
    // #2404 — Add email: the address is saved, but their form could not be sent. Retryable as-is.
    const code = body.error_code ?? body.error;
    if (code === "link_send_failed") {
      return {
        kind: "failed",
        message: "Their email address is saved, but their form could not be sent. Please try again.",
      };
    }
  }
  if (error.status === 404) {
    return {
      kind: "not_found",
      message: "That is no longer on this file. The file has been refreshed.",
    };
  }
  if (error.status === 400) {
    // #2404: the two refusals a staffer can fix in the dialog get their own words.
    const code = body.error_code ?? body.error;
    if (code === "missing_reason") {
      return { kind: "rejected", message: "Say why before you clear them." };
    }
    if (code === "invalid_email") {
      return {
        kind: "rejected",
        message: "That email address was not accepted. Check it and try again.",
      };
    }
    if (code === "missing_email") {
      return { kind: "rejected", message: "Enter their email address." };
    }
    return { kind: "rejected", message: "That was not accepted. Please check it and try again." };
  }
  return { kind: "failed", message: "That did not go through. Please try again." };
}

/**
 * A mutation for one staff action on one file. Whatever the answer — done, "already done", gone —
 * the file, the board and the chase list are refetched, so the page shows what is true now.
 */
export function useJobAction<TBody, TResult>(
  tenantId: string | null | undefined,
  jobId: string | null | undefined,
  run: (tenantId: string, jobId: string, body: TBody) => Promise<TResult>,
): UseMutationResult<TResult, Error, TBody> {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TBody>({
    mutationFn: (body: TBody) => {
      if (!tenantId || !jobId) return Promise.reject(new Error("client-portal: no file to act on"));
      return run(tenantId, jobId, body);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["job-status-detail", tenantId, jobId] }),
        queryClient.invalidateQueries({ queryKey: ["job-status-files", tenantId] }),
        queryClient.invalidateQueries({ queryKey: ["job-status-outstanding", tenantId] }),
      ]);
    },
  });
}

/** Everything the shell needs to draw itself: who, which tenant, and which reports. */
export interface PortalNavState {
  isLoading: boolean;
  error: Error | null;
  /** The intersection of (families configured) × (families this app has a page for). */
  entries: PortalFamilyEntry[];
  tenantId: string | null;
  tenantName: string | null;
  email: string | null;
}

/**
 * The one derivation both the nav rail and the landing route read.
 *
 * <p>Shared as a hook rather than computed twice because the two must never disagree: the
 * root route redirects to `entries[0]`, and the rail must be showing that same entry. React
 * Query dedupes the underlying reads by key, so composing it in two components costs one
 * fetch, not two.</p>
 */
export function usePortalNavEntries(): PortalNavState {
  const identity = useClientTenant();
  const families = useFamilies(identity.tenantId);
  // ADR-122: the Emails entry is offered to staff who may reword at least one email. A client
  // principal never asks (the engine refuses a client on that route anyway), and the shell never
  // waits for the answer: the entry appears when it arrives, after every report and board.
  const me = useMe();
  const emails = useEmailTemplates(identity.tenantId, me.data !== undefined && me.data.role !== "client");

  const entries = useMemo(() => {
    const reports = navEntriesFor(families.data?.families);
    return (emails.data?.templates.length ?? 0) > 0 ? [...reports, EMAILS_ENTRY] : reports;
  }, [families.data, emails.data]);

  // "Still loading" is defined by the ANSWER, not by a fetch flag: until the families read
  // has produced data or an error there is nothing to render, and a flag that goes false
  // between `enabled` flipping and the request starting would flash the empty state at a
  // client who does have reports.
  const familiesUnresolved =
    identity.tenantId !== null && families.data === undefined && families.error === null;

  return {
    isLoading: identity.isLoading || familiesUnresolved,
    error: identity.error ?? families.error ?? null,
    entries,
    tenantId: identity.tenantId,
    tenantName: identity.tenantName,
    email: identity.email,
  };
}

// ── ADR-122 — the Emails page: staff reword the emails their clients receive ──────────────────
//
//   GET    /internal/v0/email-templates?tenant=<uuid>                       → EmailTemplatesResponse
//   POST   /internal/v0/email-templates/{templateId}/preview?tenant=<uuid>  → EmailPreviewResponse
//   PUT    /internal/v0/email-templates/{templateId}?tenant=<uuid>          → EmailSaveResponse
//   DELETE /internal/v0/email-templates/{templateId}?tenant=<uuid>          → EmailSaveResponse
//
// WHICH emails a person may reword is the engine's answer, not this bundle's: the list carries only
// the emails whose declared role the signed-in person holds on the tenant (empty for anyone else),
// and every write is role-checked again on the engine (L-64). The page never decides who may edit.

const EMAIL_TEMPLATES_PATH = "/internal/v0/email-templates";

function emailTemplatePath(templateId: string, suffix = ""): string {
  return `${EMAIL_TEMPLATES_PATH}/${encodeURIComponent(templateId)}${suffix}`;
}

/** The emails the signed-in person may reword. `enabled` can only NARROW the tenant gate. */
export function useEmailTemplates(
  tenantId: string | null | undefined,
  enabled = true,
): UseQueryResult<EmailTemplatesResponse, Error> {
  return useQuery({
    queryKey: ["email-templates", tenantId] as const,
    queryFn: () => fetchWithAuth<EmailTemplatesResponse>(tenantScoped(EMAIL_TEMPLATES_PATH, tenantId)),
    ...REPORTING_QUERY_OPTIONS,
    enabled: !!tenantId && enabled,
  });
}

/** Render an unsaved edit (or, with no body, the wording in use) against sample values. */
export function previewEmailTemplate(
  tenantId: string,
  templateId: string,
  body: { subject?: string; body_text?: string },
): Promise<EmailPreviewResponse> {
  return fetchWithAuth<EmailPreviewResponse>(tenantScoped(emailTemplatePath(templateId, "/preview"), tenantId), {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Save the firm's wording. `based_on_version` is the version the edit started from. */
export function saveEmailTemplate(
  tenantId: string,
  templateId: string,
  body: { subject: string; body_text: string; based_on_version: string | null },
): Promise<EmailSaveResponse> {
  return fetchWithAuth<EmailSaveResponse>(tenantScoped(emailTemplatePath(templateId), tenantId), {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Go back to the default wording. */
export function resetEmailTemplate(tenantId: string, templateId: string): Promise<EmailSaveResponse> {
  return fetchWithAuth<EmailSaveResponse>(tenantScoped(emailTemplatePath(templateId), tenantId), {
    method: "DELETE",
  });
}

/** Why a save or reset did not go through, in words — and the problems, when the wording was refused. */
export interface EmailFailure {
  message: string;
  problems: { code: string; message: string; field: string | null }[];
}

export function describeEmailError(error: unknown): EmailFailure {
  if (!(error instanceof ApiError)) return { message: "That did not go through. Please try again.", problems: [] };
  let body: EmailSaveErrorBody = {};
  try {
    body = JSON.parse(error.bodyText) as EmailSaveErrorBody;
  } catch {
    body = {};
  }
  const code = body.error_code ?? body.error;
  if (error.status === 400 && code === "invalid_wording") {
    return { message: "This wording can't be saved yet.", problems: body.problems ?? [] };
  }
  if (error.status === 403) {
    const holders = (body.holders ?? []).filter((h) => h.trim() !== "");
    const who = holders.length > 0 ? joinOr(holders) : "people with the right role";
    return { message: `Only ${who} can change this email.`, problems: [] };
  }
  if (error.status === 409) {
    return {
      message:
        "Someone else changed this email after you opened it. Reload the page to see their wording, then make your change again.",
      problems: [],
    };
  }
  if (error.status === 404) return { message: "This email can no longer be changed here.", problems: [] };
  return { message: "That did not go through. Please try again.", problems: [] };
}
